import {QlBuilderModel} from "../../shared/ql/builder/ql-builder-model";
import {
    UiQlWidgetCategoryMappingDto,
    UiQlWidgetChartMappingDto,
    UiQlWidgetMapping,
    UiQlWidgetType,
} from "../model/dto/api/ui-ql-widget-mapping.dto";
import type {UiQlWidgetMappingKeys} from "../interface/ui-ql-widget-mapping-keys.interface";

/**
 * Re-exported from its own file ({@link UiQlWidgetMappingKeys}) rather than redeclared here, so
 * every existing import of `ui-ql-widget-mapping-defaults` keeps working unchanged.
 */
export type {UiQlWidgetMappingKeys} from "../interface/ui-ql-widget-mapping-keys.interface";

/**
 * Mapping helpers of the widget-type editor: which result keys a chart/category mapping may name
 * (derived from the model, no probe run needed) and a sensible default mapping per type.
 */
export class UiQlWidgetMappingDefaults {

    static keys(model: QlBuilderModel | null): UiQlWidgetMappingKeys {

        if (!model?.groupBy) {
            return {groupKeys: [], aggregateKeys: []};
        }

        const root = model.root.alias;
        const groupKeys = model.groupBy.fields.map(f => `${root}.${f.as || f.name}`);
        const aggregateKeys = model.groupBy.aggregates.map(a => `${root}.${a.as}`);

        const walk = (children: QlBuilderModel['root']['children']): void => {
            for (const child of children) {
                if (child.kind === 'join') {
                    child.fields.forEach(f => groupKeys.push(`${child.alias}.${f.as || f.name}`));
                    walk(child.children);
                }
            }
        };
        walk(model.root.children);

        return {groupKeys, aggregateKeys};
    }

    /** Default mapping of `widgetType` for `model`: `{}` for table/list, first group key / aggregate otherwise. */
    static forType(widgetType: UiQlWidgetType, model: QlBuilderModel | null): UiQlWidgetMapping {

        const {groupKeys, aggregateKeys} = UiQlWidgetMappingDefaults.keys(model);

        if (widgetType === 'chart') {
            const firstAggregate = model?.groupBy?.aggregates?.[0];
            const chart: UiQlWidgetChartMappingDto = {
                chartType: 'bar',
                chartField1st: groupKeys[0] ?? '',
                chartFieldAggregate: aggregateKeys[0] ?? '',
                chartAggregateFunction: firstAggregate?.fn ?? 'count',
            };
            return chart;
        }

        if (widgetType === 'category') {
            const category: UiQlWidgetCategoryMappingDto = {
                fieldCategory: groupKeys[0] ?? '',
                fieldCategoryCount: aggregateKeys[0] ?? '',
                displayMode: 'doughnut',
            };
            return category;
        }

        return {};
    }

    /**
     * Keep what the user chose, fill only what is missing or no longer part of the result
     * (e.g. after a grouping field was removed).
     */
    static reconcile(widgetType: UiQlWidgetType, model: QlBuilderModel | null, mapping: UiQlWidgetMapping): UiQlWidgetMapping {

        if (widgetType !== 'chart' && widgetType !== 'category') {
            return {};
        }

        const defaults = UiQlWidgetMappingDefaults.forType(widgetType, model) as { [key: string]: unknown };
        const {groupKeys, aggregateKeys} = UiQlWidgetMappingDefaults.keys(model);
        const valid = new Set([...groupKeys, ...aggregateKeys]);
        const keyFields = ['chartField1st', 'chartField2nd', 'chartFieldAggregate', 'fieldCategory', 'fieldCategoryCount'];

        const merged: { [key: string]: unknown } = {...defaults};
        for (const [key, value] of Object.entries(mapping ?? {})) {
            if (!(key in defaults) && key !== 'chartField2nd') {
                continue; // a key of another widget type
            }
            if (keyFields.includes(key) && (typeof value !== 'string' || !valid.has(value))) {
                continue; // stale result key
            }
            merged[key] = value;
        }
        return merged as unknown as UiQlWidgetMapping;
    }
}
